import { HttpBackend, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorHandler, NgModule, NgZone } from "@angular/core";
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from "@angular/material/tooltip";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { Router, RouterModule } from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { provideTanStackQuery, QueryClient } from "@tanstack/angular-query-experimental";
import { withDevtools } from "@tanstack/angular-query-experimental/devtools";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ToastModule } from "primeng/toast";

import { provideAppFeatures } from "@intocare/app-utils/lib/app-feature";
import { AuthModule } from "@intocare/auth";
import { WindowRef } from "@intocare/auth-data";
import { AWSHttpModule, S3_IMAGE_CONFIG } from "@intocare/aws";
import { CoreModule as SharedCoreModule } from "@intocare/core";
import { COMMUNICATION_DATA_API_PREFIX } from "@intocare/data-access/shared/communication";
import { provideDesignSystem } from "@intocare/design/design-system-core";
import { ENVIRONMENT_TOKEN, hqEnvironment } from "@intocare/environments";
import { CustomSentryErrorHandler } from "@intocare/errors";
import {
	CacheBustMultiTranslateHttpLoader,
	CURRENT_LANGUAGE,
	LanguageModule,
	SupportedLanguage,
} from "@intocare/language";
import { configureBufferSize, handleUndo } from "@intocare/ngrx-undo";
import { OrganisationModule } from "@intocare/organisation/data";
import { CONTEXT_TOKEN } from "@intocare/util";

import { AppComponent } from "./app.component";
import { routes } from "./app.routing";
import { CoreModule } from "./core";
import { ContextService } from "./core/services/context.service";
import {
	COMPONENT_MAP,
	CUSTOM_COMPONENT_DIRECTIVES,
	customComponentFactory,
	DynamicFormsModule,
	IcFormsModule,
} from "./core-forms";

// Ensure locale data is bundled for correct locale display values, including moment.
// TODO: Improve including locale data, so it's also correctly bundled in unit tests.
//		Preferably, we use the browser APIs so we don't have to include any locale data.
//		See https://intocare.atlassian.net/browse/ICP-18319.
import "@angular/common/locales/global/nl-BE";
import "@angular/common/locales/global/fr-BE";
// TODO: Work away the need for moment locales by using Angular's date pipe or Intl APIs.
//		We could use some things from [ngxtension](https://ngxtension.dev/utilities/intl/intl/).
//		See https://intocare.atlassian.net/browse/ICP-18314.
import "moment";
import "moment/locale/nl-be";
import "moment/locale/fr";

export function translateLoaderFactory(httpBackend: HttpBackend) {
	return new CacheBustMultiTranslateHttpLoader(httpBackend, [
		"assets/i18n/",
		"assets/shared/i18n/",
		"assets/@intocare/domain-l10n/domain-model/",
		"assets/@intocare/domain-l10n/domain-codes/",
		"assets/design/design-system-base/i18n/",
	]);
}
export function awsHttpConfigFactory() {
	return {
		region: "eu-west-1",
		apiKey: hqEnvironment.API.key,
	};
}

export function s3ImageFactory() {
	return {
		bucket: hqEnvironment.AWS.bucket,
	};
}

export function windowFactory() {
	return window;
}

export function currentLanguageFactory(translateService: TranslateService) {
	return translateService.getCurrentLang() as SupportedLanguage;
}

configureBufferSize(20);

const devtools = hqEnvironment.production ? [] : StoreDevtoolsModule.instrument({ connectInZone: true });

@NgModule({
	declarations: [AppComponent],
	bootstrap: [AppComponent],
	imports: [
		...CUSTOM_COMPONENT_DIRECTIVES,
		BrowserModule,
		BrowserAnimationsModule,
		RouterModule.forRoot(routes, { bindToComponentInputs: true, paramsInheritanceStrategy: "always" }),
		CoreModule.forRoot(),
		SharedCoreModule.forRoot(),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: translateLoaderFactory,
				deps: [HttpBackend],
			},
		}),
		StoreModule.forRoot(
			{},
			{
				metaReducers: [handleUndo],
				runtimeChecks: {
					strictStateImmutability: false,
					strictActionImmutability: false,
				},
			},
		),
		devtools,
		AWSHttpModule.forRoot(awsHttpConfigFactory),
		LanguageModule.forRoot(),
		AuthModule,
		OrganisationModule,
		IcFormsModule,
		DynamicFormsModule.forRoot({
			components: {
				provide: COMPONENT_MAP,
				useFactory: customComponentFactory,
			},
		}),
		ServiceWorkerModule.register("ngsw-worker.js", {
			enabled: hqEnvironment.PWA,
		}),
		ToastModule,
		ConfirmDialogModule,
	],
	providers: [
		{ provide: WindowRef, useFactory: windowFactory },
		{ provide: S3_IMAGE_CONFIG, useFactory: s3ImageFactory },
		{
			provide: ErrorHandler,
			useClass: CustomSentryErrorHandler,
			deps: [NgZone, Router],
		},
		{
			provide: CURRENT_LANGUAGE,
			deps: [TranslateService],
			useFactory: currentLanguageFactory,
		},
		{
			provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
			useValue: {
				disableTooltipInteractivity: true,
			},
		},
		{
			provide: ENVIRONMENT_TOKEN,
			useValue: hqEnvironment,
		},
		provideAppFeatures(hqEnvironment.APP_FEATURES ?? {}),
		{ provide: COMMUNICATION_DATA_API_PREFIX, useValue: "/communication/hq" },
		provideTanStackQuery(
			new QueryClient({
				defaultOptions: {
					queries: { retry: false, throwOnError: true },
					mutations: { retry: false, throwOnError: true },
				},
			}),
			withDevtools(),
		),
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: CONTEXT_TOKEN, useExisting: ContextService },
		...provideDesignSystem(),
	],
})
export class AppModule {}
